import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

const DEFAULT_STATE = {
  isAuthenticated: false,
  isLoading: false,
  token: null,
  user: null,
  error: null,
  remember: false,
};

const useAuthStore = create(
  persist(
    (set, get) => ({
      ...DEFAULT_STATE,

      setRemember: (remember) => {
        try {
          localStorage.setItem('auth-remember', remember ? 'true' : 'false');
        } catch (e) {}
        set({ remember });
      },

      login: async (credentials) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/auth/login', credentials);
          const token = response.data?.access_token || response.data?.token || response.data?.accessToken || null;
          const user = response.data?.user || response.data?.profile || response.data || null;

          set({ isAuthenticated: !!token, token, user, isLoading: false, error: null });

          if (token) api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          return { success: true, user };
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Login failed';
          set({ isAuthenticated: false, token: null, user: null, isLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      register: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/auth/register', userData);
          const token = response.data?.access_token || response.data?.token || response.data?.accessToken || null;
          const user = response.data?.user || response.data?.profile || response.data || null;

          set({ isAuthenticated: !!token, token, user, isLoading: false, error: null });

          if (token) api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          return { success: true, user };
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Registration failed';
          set({ isAuthenticated: false, token: null, user: null, isLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      loginWithGoogle: (token, user) => {
        set({ isAuthenticated: true, token, user, isLoading: false, error: null });
        if (token) api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      },

      logout: () => {
        set({ isAuthenticated: false, token: null, user: null, error: null });
        try {
          delete api.defaults.headers.common['Authorization'];
        } catch (e) {}
      },

// ✅ FIXED: Remove API call, just update local state
updateProfile: (userData) => {
  set({ user: userData });
  return { success: true, user: userData };
},

      refreshToken: async () => {
        const { token } = get();
        if (!token) return false;
        try {
          const res = await api.get('/auth/me');
          const user = res.data?.user || res.data || null;
          set({ isAuthenticated: true, user, error: null });
          return true;
        } catch (error) {
          set({ isAuthenticated: false, token: null, user: null, error: 'Session expired' });
          try { delete api.defaults.headers.common['Authorization']; } catch (e) {}
          return false;
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      getStorage: () => {
        return {
          getItem: (name) => {
            try {
              const remember = localStorage.getItem('auth-remember') === 'true';
              const storage = remember ? localStorage : sessionStorage;
              return storage.getItem(name);
            } catch (e) {
              return null;
            }
          },
          setItem: (name, value) => {
            try {
              const remember = localStorage.getItem('auth-remember') === 'true';
              const storage = remember ? localStorage : sessionStorage;
              storage.setItem(name, value);
            } catch (e) {}
          },
          removeItem: (name) => {
            try { localStorage.removeItem(name); sessionStorage.removeItem(name); } catch (e) {}
          },
        };
      },
    }
  )
);

// Subscribe to token changes so axios always has the latest Authorization header
useAuthStore.subscribe((state) => state.token, (token) => {
  try {
    if (token) api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    else delete api.defaults.headers.common['Authorization'];
  } catch (e) {}
});

export default useAuthStore;